import React, { useState, useEffect } from 'react';
import { usePersistedState } from '../../hooks/usePersistedState';
import { useWeb3React } from '@web3-react/core';
import { MetaMask } from '@web3-react/metamask';
import { CoinbaseWallet } from '@web3-react/coinbase-wallet';
import { ethers } from 'ethers';
import { Alert, Card, CardContent, Typography, Button, Grid2, Box, Tooltip, Chip } from '@mui/material'; // Import MUI components
import MuiAlert from '@mui/material/Alert';  // Add this specific import for Alert
import SignatureProgressDisplay from './SignatureProgressDisplay';
import { useSignatureContext } from '../context/SignatureContext';
import { useWallet } from '../context/WagmiWalletContext';
import type { WalletType } from '../context/WagmiWalletContext';
import { RainbowKitCustomConnectButton } from '../wallet/RainbowKitCustomConnectButton';
import { useWalletAddresses } from '../../hooks/useWalletAddresses';
import logger from '../../utils/logger';

interface CollectedSignature {
    signer: string;
    signature: string;
}


const RPC_URLS: Record<string, string> = {
    '31337': 'http://localhost:8545',
    '8453': 'https://base-mainnet.g.alchemy.com/v2/g5tQkTXHhXy00cHmVvh0c5beL_n_q6dR',
    '84532': 'https://base-sepolia.g.alchemy.com/v2/2n8z3_g69ZJkpmhRo4y2NiC0H9Xr7_1r'
};

interface MultiSigWalletSignatureFlowProps {
    selectedContract: {
        address: string;
        name: string; // Add this line
        abi: Array<{ type: string; name: string }>;
    };
    signer: ethers.Signer | null;
    chainId: string;
    functionInputs: { [key: string]: string[] };
    owners: string[];
    onSignaturesCollected: (signatures: CollectedSignature[]) => void;
    onSignerChanged: (newSigner: ethers.Signer) => void;
}

interface WalletState {
    currentOwner: string;
    requiredWallet: WalletType;
    isConnected: boolean;
    hasSignature: boolean;
    needsWalletSwitch: boolean; // Track if wallet switch is needed
    needsExtensionSwitch: boolean; // Track if extension switch is needed
}

// First, let's explicitly define the type to see what's actually allowed
type CoinbaseWalletOptions = {
    appName: string;
    chainId?: number;
    supportedChainIds?: number[];
    // Add other known properties here
};

const MultiSigWalletSignatureFlow: React.FC<MultiSigWalletSignatureFlowProps> = ({
    selectedContract,
    signer,
    chainId,
    functionInputs, // now coming from props
    onSignaturesCollected,
    onSignerChanged
}) => {
    // Fetch wallet addresses from backend
    const { addresses: walletAddresses, loading: addressesLoading, error: addressesError } = useWalletAddresses(chainId);

    const [walletStates, setWalletStates] = useState<WalletState[]>([]);
    const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null);
    const [status, setStatus] = useState<string>('');
    const [signatures, setSignatures] = useState<{ [address: string]: string }>({});
    const { connector, account } = useWeb3React();
    const { activeWalletType, switchWalletType } = useWallet(); // Use our enhanced wallet context
    const [owners, setOwners] = useState<string[]>([]);
    const [currentSignerIndex, setCurrentSignerIndex] = useState(0);
    const [executionStatus, setExecutionStatus] = useState('');
    const [threshold, setThreshold] = useState<number>(0);
    const [showSignatureFlow, setShowSignatureFlow] = usePersistedState('show_signature_flow', false);
    const [tempSignatureState, setTempSignatureState] = usePersistedState<any>('signature_state_before_collect', null);
    const [showRecoveryWarning, setShowRecoveryWarning] = useState(false);

    const {
        signatures: contextSignatures,
        addSignature,
        hasSignature,
        getAllSignatures,
        updateSignatureProgress,
        clearAllStoredData
    } = useSignatureContext();

    // Build the wallet mapping from fetched addresses
    const buildWalletMapping = (): Record<string, WalletType> => {
        const mapping: Record<string, WalletType> = {};

        // Map based on environment (chainId)
        if (chainId === '31337') {
            // Anvil fork
            if (walletAddresses.ANVIL_ACCOUNT_0)
                mapping[walletAddresses.ANVIL_ACCOUNT_0.toLowerCase()] = 'metamask';
            if (walletAddresses.ANVIL_ACCOUNT_1)
                mapping[walletAddresses.ANVIL_ACCOUNT_1.toLowerCase()] = 'coinbase';
            if (walletAddresses.TREZOR_ACCOUNT)
                mapping[walletAddresses.TREZOR_ACCOUNT.toLowerCase()] = 'metamask';
            if (walletAddresses.ANVIL_ACCOUNT_3)
                mapping[walletAddresses.ANVIL_ACCOUNT_3.toLowerCase()] = 'metamask';
        } else if (chainId === '84532') {
            // Base Sepolia
            if (walletAddresses.SEPOLIA_METAMASK_ACCOUNT_1)
                mapping[walletAddresses.SEPOLIA_METAMASK_ACCOUNT_1.toLowerCase()] = 'metamask';
            if (walletAddresses.SEPOLIA_COINBASE_ACCOUNT_1)
                mapping[walletAddresses.SEPOLIA_COINBASE_ACCOUNT_1.toLowerCase()] = 'coinbase';
            if (walletAddresses.SEPOLIA_METAMASK_ACCOUNT_2)
                mapping[walletAddresses.SEPOLIA_METAMASK_ACCOUNT_2.toLowerCase()] = 'metamask';
            if (walletAddresses.SEPOLIA_COINBASE_ACCOUNT_2)
                mapping[walletAddresses.SEPOLIA_COINBASE_ACCOUNT_2.toLowerCase()] = 'coinbase';
        } else if (chainId === '8453') {
            // Base Mainnet
            if (walletAddresses.METAMASK_ACCOUNT)
                mapping[walletAddresses.METAMASK_ACCOUNT.toLowerCase()] = 'metamask';
            if (walletAddresses.COINBASE_ACCOUNT)
                mapping[walletAddresses.COINBASE_ACCOUNT.toLowerCase()] = 'coinbase';
            if (walletAddresses.TREZOR_ACCOUNT)
                mapping[walletAddresses.TREZOR_ACCOUNT.toLowerCase()] = 'metamask';
            if (walletAddresses.LEDGER_ACCOUNT)
                mapping[walletAddresses.LEDGER_ACCOUNT.toLowerCase()] = 'metamask';
        }

        logger.log('Built wallet mapping for chain', chainId, ':', Object.keys(mapping).length, 'addresses');
        return mapping;
    };

    // Use the dynamic mapping
    const KNOWN_WALLET_ADDRESSES = buildWalletMapping();

    logger.log("MultiSigWalletSignatureFlow rendering with:", {
        contractAddress: selectedContract?.address,
        hasSigner: !!signer,
        chainId,
        ownersCount: owners.length,
        contextSignaturesCount: Object.keys(contextSignatures).length,
        activeWalletType,
        addressesLoaded: !addressesLoading,
        walletMappingSize: Object.keys(KNOWN_WALLET_ADDRESSES).length
    });

    useEffect(() => {
        // Check current wallet to see if it matches one of our owners
        const checkCurrentWallet = async () => {
            if (!window.ethereum) return;

            try {
                const accounts = await (window.ethereum as any).request({ method: 'eth_requestAccounts' });
                if (accounts && accounts.length > 0) {
                    const currentAddress = accounts[0].toLowerCase();

                    // Find matching owner
                    const matchingOwnerIndex = owners.findIndex(
                        owner => owner.toLowerCase() === currentAddress
                    );

                    if (matchingOwnerIndex !== -1) {
                        logger.log(`Current wallet matches owner at index ${matchingOwnerIndex}`);
                        setCurrentSignerIndex(matchingOwnerIndex);

                        // Update wallet states to reflect current connection
                        setWalletStates(prev =>
                            prev.map((state, idx) => ({
                                ...state,
                                isConnected: idx === matchingOwnerIndex
                            }))
                        );
                    }
                }
            } catch (error) {
                logger.error("Error checking current wallet:", error);
            }
        };

        if (owners.length > 0) {
            checkCurrentWallet();
        }
    }, [owners, signer]);

    // Initialize the context signature progress when owners are loaded
    useEffect(() => {
        if (owners && owners.length > 0) {
            updateSignatureProgress({
                owners: owners,
                requiredCount: threshold,
                collectedCount: 0,
                completedSigners: []
            });
            initializeWalletStates();
        }
    }, [owners, threshold]);

    // Fetch owners from the MultiSig wallet contract
    useEffect(() => {
        const fetchOwners = async () => {
            if (!selectedContract || !signer) return;
            try {
                const filteredABI = selectedContract.abi.filter(
                    (item) => item.type === 'function' && item.name === 'getOwners'
                );
                const ownerContract = new ethers.Contract(
                    selectedContract.address,
                    filteredABI,
                    signer
                );
                const fetchedOwners = await ownerContract.getOwners();
                setOwners(fetchedOwners);
                logger.log("MultiSig Wallet Owners Collected");
                // logger.log("MultiSig Wallet Owners:", fetchedOwners);
            } catch (error: any) {
                logger.error("Error fetching owners:", error);
            }
        };

        // Only fetch if signer is available - don't throw error
        if (signer) {
            fetchOwners();
        }
    }, [selectedContract, signer]);

    useEffect(() => {
        if (!selectedContract || !signer) return;
        const fetchThreshold = async () => {
            try {
                const filteredABI = selectedContract.abi.filter(
                    (item) => item.type === 'function' && item.name === 'getThreshold'
                );
                const contract = new ethers.Contract(selectedContract.address, filteredABI, signer);
                const t = await contract.getThreshold();
                setThreshold(parseInt(t.toString()));
            } catch (error: any) {
                logger.error("Error fetching threshold:", error);
            }
        };

        fetchThreshold();
    }, [selectedContract, signer]);

    useEffect(() => {
        if (owners && owners.length > 0) {
            initializeWalletStates();
        }
    }, [owners, activeWalletType]); // Re-initialize when activeWalletType changes

    // Smart recovery warning logic - only show if legitimately interrupted
    useEffect(() => {
        const checkRecoveryWarning = () => {
            const recoveryKey = localStorage.getItem('signature_state_before_collect');

            if (!recoveryKey) {
                setShowRecoveryWarning(false);
                return;
            }

            try {
                const savedState = JSON.parse(recoveryKey);

                // Only show warning if:
                // 1. Saved state exists
                // 2. It's for the current contract
                // 3. There are actually incomplete signatures
                const isCurrentContract = savedState.selectedContract?.address?.toLowerCase() ===
                    selectedContract?.address?.toLowerCase();

                if (!isCurrentContract) {
                    // Old state from different contract - clean it up
                    localStorage.removeItem('signature_state_before_collect');
                    setShowRecoveryWarning(false);
                    return;
                }

                // Check if there are actually pending signatures
                const savedSignatures = localStorage.getItem('multisig_signatures');
                if (savedSignatures) {
                    const parsedSigs = JSON.parse(savedSignatures);
                    const signatureCount = Object.keys(parsedSigs).length;

                    // Only show if we have some signatures but not all
                    if (signatureCount > 0 && signatureCount < threshold && threshold > 0) {
                        setShowRecoveryWarning(true);
                    } else {
                        // Either no signatures or all complete - clean up
                        localStorage.removeItem('signature_state_before_collect');
                        setShowRecoveryWarning(false);
                    }
                } else {
                    // No signatures at all - clean up
                    localStorage.removeItem('signature_state_before_collect');
                    setShowRecoveryWarning(false);
                }
            } catch (error) {
                logger.error("Error checking recovery warning:", error);
                setShowRecoveryWarning(false);
            }
        };

        if (selectedContract && threshold > 0) {
            checkRecoveryWarning();
        }
    }, [selectedContract, threshold]);

    // Enhance the useEffect for loading signatures
    useEffect(() => {
        // When signer changes (wallet switch), preserve our signing state
        const preserveSigningState = async () => {
            try {
                // Get current wallet address
                const currentAddress = await signer?.getAddress();
                logger.log("Wallet changed to:", currentAddress);

                // Get stored signature progress
                const savedProgress = localStorage.getItem('multisig_signature_progress');
                const savedSignatures = localStorage.getItem('multisig_signatures');

                if (savedProgress && savedSignatures) {
                    const progress = JSON.parse(savedProgress);
                    const signatures = JSON.parse(savedSignatures);

                    // Update wallet states to reflect current signature state
                    setWalletStates(prevStates =>
                        prevStates.map(state => ({
                            ...state,
                            // Mark as signed if we have a signature for this owner
                            hasSignature: !!signatures[state.currentOwner.toLowerCase()],
                            // Mark as connected if it matches current wallet
                            isConnected: state.currentOwner.toLowerCase() === currentAddress?.toLowerCase(),
                            // Check if extension switch is needed
                            needsExtensionSwitch: state.requiredWallet !== activeWalletType
                        }))
                    );

                    // Update the current signer index
                    const newIndex = owners.findIndex(
                        owner => owner.toLowerCase() === currentAddress?.toLowerCase()
                    );
                    if (newIndex !== -1) {
                        setCurrentSignerIndex(newIndex);
                    }
                } else {
                    // If no saved signatures, ensure wallet states are properly initialized
                    initializeWalletStates();
                }
            } catch (error) {
                logger.error("Error preserving signing state:", error);
            }
        };

        if (signer && owners.length > 0) {
            preserveSigningState();
        }
    }, [signer, owners, activeWalletType]);

    // Get current account from any connected wallet - ALWAYS returns lowercase
    const getCurrentAccount = async () => {
        if (window.ethereum) {
            try {
                // Request accounts from the current wallet extension
                const accounts = await (window.ethereum as any).request({ method: 'eth_requestAccounts' });

                if ((window.ethereum as any).isCoinbaseWallet) {
                    logger.log("Coinbase Wallet accounts identified:", accounts);

                    // For Coinbase Wallet, try to get additional account info if available
                    try {
                        // Some Coinbase Wallet versions expose additional methods
                        if ((window.ethereum as any).getAddressHexFromResolved) {
                            const resolvedAccounts = await Promise.all(
                                accounts.map(async (account: string) => {
                                    try {
                                        // Try to resolve any shortnames to their hex addresses
                                        const hexAddress = await (window.ethereum as any).getAddressHexFromResolved(account);
                                        logger.log(`Resolved ${account} to ${hexAddress}`);
                                        // CRITICAL: Normalize to lowercase
                                        return (hexAddress || account).toLowerCase();
                                    } catch (e) {
                                        // CRITICAL: Normalize to lowercase
                                        return account.toLowerCase();
                                    }
                                })
                            );
                            logger.log("Resolved Coinbase accounts (normalized):", resolvedAccounts);
                            if (resolvedAccounts && resolvedAccounts.length > 0) {
                                return resolvedAccounts[0];
                            }
                        }
                    } catch (resolveError) {
                        logger.error("Error resolving Coinbase account names:", resolveError);
                        // Fall back to regular accounts
                    }
                } else if ((window.ethereum as any).isMetaMask) {
                    logger.log("MetaMask accounts identified:", accounts);
                } else {
                    logger.log("Unknown wallet accounts identified:", accounts);
                }

                if (accounts && accounts.length > 0) {
                    // CRITICAL: Always return lowercase for consistent comparison
                    return accounts[0].toLowerCase();
                }
            } catch (error) {
                logger.error("Error requesting accounts directly:", error);
            }
        }
        return null;
    };

    const initializeWalletStates = () => {
        const newStates = owners.map((owner: string) => {
            const lowerOwner = owner.toLowerCase();
            const existingState = walletStates.find(
                (s) => s.currentOwner.toLowerCase() === lowerOwner
            );

            // Check the context for existing signatures
            const hasSig = hasSignature(lowerOwner);

            // Determine if this owner requires a different wallet extension than currently active
            const requiredWallet = KNOWN_WALLET_ADDRESSES[lowerOwner] || 'metamask'; // Default to metamask if not specified
            const needsExtensionSwitch = activeWalletType !== requiredWallet;

            return {
                currentOwner: owner,
                requiredWallet: requiredWallet,
                isConnected: existingState ? existingState.isConnected : false,
                hasSignature: hasSig,
                needsWalletSwitch: existingState ? existingState.needsWalletSwitch : false,
                needsExtensionSwitch: needsExtensionSwitch
            };
        });

        setWalletStates(newStates);
        setStatus(`Retrieved ${owners.length} owners. Starting signature collection...`);
        logger.log("Initialized wallet states:", newStates);
    };

    const prepareTxForSignature = async (contract: ethers.Contract, inputs: string[]) => {
        // logger.log("Raw execTransaction inputs:", inputs);

        // Validate the 'to' address
        if (!inputs[0]) {
            throw new Error("Missing 'to' address in inputs");
        }

        // Make a copy of inputs to avoid mutating the original array
        const processedInputs = [...inputs];

        // Validate address format for 'to'
        try {
            processedInputs[0] = ethers.utils.getAddress(processedInputs[0]);
            logger.log("Address normalized:", processedInputs[0]);
        } catch (error) {
            logger.error("Invalid 'to' address:", processedInputs[0], error);
            throw new Error(`Invalid 'to' address: ${processedInputs[0]}`);
        }

        // Validate and fix data field
        if (processedInputs[2]) {
            // Import secureDataHandler functions using dynamic import
            try {
                // Use dynamic import instead of require
                const secureDataHandlerModule = await import('../../utils/secureDataHandler');
                const { isEncrypted, autoDecryptIfNeeded } = secureDataHandlerModule;

                if (isEncrypted(processedInputs[2])) {
                    try {
                        logger.log("Detected encrypted data, attempting to decrypt");
                        // Attempt to decrypt the data
                        const decryptedData = autoDecryptIfNeeded(processedInputs[2]);

                        if (decryptedData !== processedInputs[2]) {
                            logger.log("Successfully decrypted data");
                            processedInputs[2] = decryptedData;
                        } else {
                            logger.warn("Data appears to be encrypted but couldn't be auto-decrypted. May need password.");
                            // If we couldn't decrypt, we'll continue with the original data
                            // but this might cause issues later
                        }
                    } catch (decryptError) {
                        logger.error("Error decrypting data:", decryptError);
                        // Continue with original data, but this might cause issues
                    }
                }
            } catch (importError) {
                logger.error("Error importing secureDataHandler:", importError);
                // Continue without decryption if module can't be imported
            }

            // Ensure it starts with 0x
            if (!processedInputs[2].startsWith('0x')) {
                throw new Error(`Data field must start with 0x. Received: ${processedInputs[2].substring(0, 50)}... This indicates the data was not properly decrypted or formatted.`);
            }

            // Remove 0x prefix for validation
            const hexData = processedInputs[2].substring(2);

            // Validate hex data - FAIL FAST if invalid
            const validHex = /^[0-9a-f]*$/i;
            if (!validHex.test(hexData)) {
                throw new Error(`Invalid hex data detected: ${hexData.substring(0, 50)}... This indicates the data was not properly decrypted. Data must contain only hexadecimal characters (0-9, a-f).`);
            }

            // Validate even length
            if (hexData.length % 2 !== 0) {
                throw new Error(`Hex data must have even length. Received length: ${hexData.length}. This indicates malformed data.`);
            }

            logger.log("✅ Data validation passed:", processedInputs[2].substring(0, 10) + "...");
        } else {
            // Default empty data
            processedInputs[2] = '0x';
        }

        // Validate and process value - handle both string and BigNumber inputs
        if (processedInputs[1] !== undefined && processedInputs[1] !== null) {
            // Check if it's already a BigNumber object (multiple possible formats)
            if (typeof processedInputs[1] === 'object' && processedInputs[1] !== null) {
                const valueObj = processedInputs[1] as any;

                // Handle ethers BigNumber format: {type: "BigNumber", hex: "0x00"}
                if (valueObj.type === 'BigNumber' && valueObj.hex !== undefined) {
                    // Convert hex to decimal string
                    const decimalValue = parseInt(valueObj.hex, 16).toString();
                    processedInputs[1] = decimalValue;
                    logger.log("Converted BigNumber object to string:", processedInputs[1]);
                }
                // Handle other BigNumber-like objects
                else if (valueObj._isBigNumber || valueObj._hex !== undefined) {
                    processedInputs[1] = ethers.BigNumber.from(valueObj).toString();
                    logger.log("Converted BigNumber-like object to string:", processedInputs[1]);
                }
                // If it's some other object, default to "0"
                else {
                    logger.warn("Unknown object type for value - defaulting to 0:", processedInputs[1]);
                    processedInputs[1] = "0";
                }
            }
            // Handle string values
            else if (typeof processedInputs[1] === 'string') {
                // Check if it's a valid numeric string
                if (!/^[\d.]+$/.test(processedInputs[1])) {
                    logger.warn("Invalid value string detected - defaulting to 0:", processedInputs[1]);
                    processedInputs[1] = "0";
                }
            }
            // Handle numeric values
            else if (typeof processedInputs[1] === 'number') {
                processedInputs[1] = (processedInputs[1] as number).toString();
                logger.log("Converted number to string:", processedInputs[1]);
            }
            // Default case
            else {
                logger.warn("Unexpected value type - defaulting to 0:", typeof processedInputs[1], processedInputs[1]);
                processedInputs[1] = "0";
            }
        } else {
            logger.warn("No value provided - defaulting to 0");
            processedInputs[1] = "0";
        }

        // logger.log("Processed execTransaction inputs:", processedInputs);

        // Prepare transaction parameters from processed inputs
        const txParams = {
            to: processedInputs[0],
            value: ethers.utils.parseEther(processedInputs[1]),
            data: processedInputs[2],
            operation: parseInt(processedInputs[3] || "0") || 0,
            safeTxGas: processedInputs[4] || "0",
            baseGas: processedInputs[5] || "0",
            gasPrice: processedInputs[6] || "0",
            gasToken: processedInputs[7] || ethers.constants.AddressZero,
            refundReceiver: processedInputs[8] || ethers.constants.AddressZero,
        };

        try {
            // Get current nonce
            const nonce = await contract.nonce();
            logger.log("Current nonce:", nonce.toString());

            // Log the sanitized parameters
            logger.log("Transaction parameters after validation:", {
                to: txParams.to,
                value: txParams.value.toString(),
                dataPrefix: txParams.data.substring(0, 10),
                dataLength: txParams.data.length,
                operation: txParams.operation,
            });

            // Get transaction hash that needs to be signed
            // CRITICAL FIX: Convert BigNumber to string for contract call
            const txHash = await contract.getTransactionHash(
                txParams.to,
                txParams.value.toString(), // Convert BigNumber to string
                txParams.data,
                txParams.operation,
                txParams.safeTxGas,
                txParams.baseGas,
                txParams.gasPrice,
                txParams.gasToken,
                txParams.refundReceiver,
                nonce
            );

            logger.log("Transaction hash calculated successfully:", txHash);
            return { txParams, txHash, nonce };
        } catch (error) {
            logger.error("Error in transaction preparation:", error);

            // More detailed error information
            if (error instanceof Error && error.message.includes("invalid arrayify value")) {
                logger.error("Invalid hex data detected. Transaction parameters:", {
                    to: txParams.to,
                    value: txParams.value.toString(),
                    data: txParams.data,
                    dataLength: txParams.data.length,
                    operation: txParams.operation
                });
            }

            throw error;
        }
    };

    // Enhanced function to switch to a wallet by its address with improved extension switching
    const switchToWallet = async (walletAddress: string) => {
        try {
            // Get the current account state first
            const directAccount = await getCurrentAccount();
            logger.log("Current account before switch:", directAccount);

            if (directAccount?.toLowerCase() === walletAddress.toLowerCase()) {
                logger.log("Already connected to the correct account - no need to switch");
                // Update the signer to ensure we have the latest context
                if (window.ethereum) {
                    const provider = new ethers.providers.Web3Provider(window.ethereum as any);
                    setProvider(provider);
                    const newSigner = provider.getSigner();
                    onSignerChanged(newSigner);
                    return provider;
                }
            }

            // Get the required wallet type for this address
            const walletType = KNOWN_WALLET_ADDRESSES[walletAddress.toLowerCase()] || 'metamask';
            const walletName = walletType === 'coinbase' ? 'Coinbase Wallet' : 'MetaMask';

            // Check if we need to switch wallet extensions
            if (walletType && activeWalletType !== walletType) {
                logger.log(`Need to switch from ${activeWalletType || 'unknown'} to ${walletType} extension`);
                setStatus(`Switching to ${walletType} wallet extension...`);

                // Show a more detailed message to the user
                const message = `Please switch to your ${walletName} extension to continue.\n\n` +
                    `1. Click on the browser extension icon in your toolbar\n` +
                    `2. Select the ${walletName} extension\n` +
                    `3. Make sure you're connected to the correct account: ${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`;

                alert(message);

                // Attempt to switch wallet type using our enhanced context
                try {
                    const switched = await switchWalletType(walletType);
                    if (!switched) {
                        throw new Error(`Failed to switch to ${walletType} wallet extension`);
                    }
                    logger.log(`Successfully switched to ${walletType} extension`);
                } catch (switchError) {
                    logger.error(`Error switching to ${walletType} extension:`, switchError);

                    // Even if automatic switching fails, give the user a chance to switch manually
                    alert(`Automatic wallet switching failed. Please manually switch to ${walletName} and then click OK to continue.`);

                    // Wait a moment to give user time to switch manually
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }

            // Now handle account switching within the extension
            const accountMessage = `Please switch to the wallet address ${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)} in your ${walletName} extension.`;
            alert(accountMessage);

            // Wait a moment to give user time to switch
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Check if they've switched
            const updatedAccount = await getCurrentAccount();
            logger.log("Account after manual switch:", updatedAccount);

            // Create new provider after switch
            if (window.ethereum) {
                const provider = new ethers.providers.Web3Provider(window.ethereum as any);
                setProvider(provider);
                const newSigner = provider.getSigner();
                onSignerChanged(newSigner);

                const newIndex = owners.findIndex(addr =>
                    addr.toLowerCase() === walletAddress.toLowerCase()
                );
                if (newIndex !== -1) setCurrentSignerIndex(newIndex);

                // Update wallet states to reflect the current connection
                setWalletStates(prev =>
                    prev.map((state, idx) => ({
                        ...state,
                        isConnected: idx === newIndex,
                        // Update needsExtensionSwitch based on current wallet type
                        needsExtensionSwitch: state.requiredWallet !== activeWalletType
                    }))
                );

                return provider;
            }

            throw new Error('Failed to get provider');
        } catch (error: any) {
            logger.error("Error switching wallet:", error);
            setExecutionStatus(`Error switching wallet: ${error.message}`);
            setStatus(`Failed to switch wallet: ${error.message}`);
            // Optionally rethrow to allow caller to handle
            throw error;
        }
    };

    // Enhanced collectSignature function to handle wallet extension switching
    async function collectSignature(walletState: WalletState) {
        logger.log("Collecting signature for:", walletState.currentOwner.substring(0, 10) + "...");

        // CRITICAL: Save the current state before any potential refreshes
        try {
            // Create a comprehensive state snapshot
            const signatureState = {
                signatures: {
                    ...signatures,
                    // Add any signatures from context here
                    ...contextSignatures
                },
                currentOwner: walletState.currentOwner,
                inputs: functionInputs,
                threshold,
                owners,
                walletStates,
                selectedContract: {
                    address: selectedContract.address,
                    name: selectedContract.name || ''
                },
                timestamp: Date.now()
            };

            // Create a unique key to avoid collisions
            const stateKey = `signature_flow_${selectedContract.address}_${Date.now()}`;

            // Save the full state
            localStorage.setItem(stateKey, JSON.stringify(signatureState));

            // Save a reference to the latest state key
            localStorage.setItem('current_signature_flow_key', stateKey);

            logger.log("Successfully preserved signature state before wallet switch");
        } catch (err) {
            logger.error("Failed to preserve state before collection:", err);
        }

        // Get current account from the active wallet
        const directAccount = await getCurrentAccount();
        logger.log("Direct wallet account:", directAccount);

        // Ensure we have a signer
        if (!signer) {
            setStatus('No signer available. Please connect wallet.');
            setExecutionStatus('Error: No signer available');
            return;
        }

        try {
            // Use direct account if available, fall back to context account
            const currentAccount = directAccount?.toLowerCase() || account?.toLowerCase();
            const requiredOwner = walletState.currentOwner.toLowerCase();

            // Add debugging to see exactly what's being compared
            logger.log("Signature address comparison:", {
                directAccount,
                contextAccount: account,
                currentAccountUsed: currentAccount,
                requiredOwner,
                isMatch: currentAccount === requiredOwner
            });

            // If we already have a signature for this owner, let the user know
            if (hasSignature(requiredOwner)) {
                setStatus(`Already collected signature for ${requiredOwner}`);
                return;
            }

            // Check if we need to switch wallet extension
            if (walletState.needsExtensionSwitch) {
                setStatus(`Switching to ${walletState.requiredWallet} wallet extension for ${requiredOwner}`);

                // Try to switch wallet extension
                await switchToWallet(walletState.currentOwner);

                // After switching, we need to update our references
                const newAccount = await getCurrentAccount();
                if (!newAccount) {
                    throw new Error('Failed to get account after wallet extension switch');
                }
            }
            // Check if we need to switch wallets within the same extension
            else if (currentAccount !== requiredOwner) {
                setStatus(`Switching to required wallet for ${requiredOwner}`);
                setStatus(`Please switch to wallet address ${walletState.currentOwner} in your ${walletState.requiredWallet === 'coinbase' ? 'Coinbase Wallet' : 'MetaMask'} extension.`);
                alert(`Please switch to the wallet address ${walletState.currentOwner} in your ${walletState.requiredWallet === 'coinbase' ? 'Coinbase Wallet' : 'MetaMask'} extension.`);

                // Give user time to switch manually, which won't cause a refresh
                await new Promise(resolve => setTimeout(resolve, 3000));

                // Re-check the current account after the user has had time to switch
                const newAccount = await getCurrentAccount();
                logger.log("Account after manual switch:", {
                    newAccount,
                    requiredOwner,
                    isLowerCaseMatch: newAccount?.toLowerCase() === requiredOwner,
                    isCaseInsensitiveMatch: newAccount?.toLowerCase() === requiredOwner.toLowerCase()
                });

                // More flexible matching - try both exact lowercase match and case-insensitive match
                if (newAccount?.toLowerCase() !== requiredOwner &&
                    newAccount?.toLowerCase() !== requiredOwner.toLowerCase()) {

                    // Special handling for Coinbase Wallet with ENS/shortnames
                    if (walletState.requiredWallet === 'coinbase' && window.ethereum &&
                        (window.ethereum as any).isCoinbaseWallet) {

                        // Give user more specific instructions for Coinbase Wallet
                        alert(`Please ensure you're using the correct account in Coinbase Wallet that corresponds to address ${walletState.currentOwner}. If you're using a shortname like starbankfi.cb.id, make sure it's properly linked to this address.`);

                        // Give one more chance after alert
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        const finalAccount = await getCurrentAccount();

                        // Log detailed comparison for debugging
                        logger.log("Final account check:", {
                            finalAccount,
                            requiredOwner,
                            walletCurrentOwner: walletState.currentOwner,
                            isExactMatch: finalAccount === requiredOwner,
                            isLowerMatch: finalAccount?.toLowerCase() === requiredOwner,
                            isCaseInsensitiveMatch: finalAccount?.toLowerCase() === requiredOwner.toLowerCase()
                        });

                        // Final check with more flexible matching
                        if (finalAccount?.toLowerCase() !== requiredOwner &&
                            finalAccount?.toLowerCase() !== requiredOwner.toLowerCase()) {
                            throw new Error(`Wallet not switched to required owner: ${walletState.currentOwner}`);
                        }
                    } else {
                        throw new Error(`Wallet not switched to required owner: ${walletState.currentOwner}`);
                    }
                }

                logger.log("✅ Wallet successfully switched to required owner");
            } else {
                logger.log("Already using the correct wallet - no switch needed");
            }

            // Verify signer again after wallet switch
            if (!signer) {
                throw new Error('Signer could not be established');
            }

            const contract = new ethers.Contract(
                selectedContract.address,
                selectedContract.abi,
                signer
            );

            // CRITICAL FIX: Read from localStorage where FunctionExplorer stored the decrypted data
            // DO NOT read from functionInputs prop which may still contain KV references
            const storedData = localStorage.getItem('function_inputs');

            if (!storedData) {
                throw new Error('No function inputs found in localStorage. Please execute the transaction from Function Explorer first.');
            }

            let inputs: string[] = [];
            let detectedFunctionName = '';

            try {
                const parsedData = JSON.parse(storedData);
                inputs = parsedData.inputs || [];
                detectedFunctionName = parsedData.functionName || '';

                logger.log(`✅ Loaded execution-ready inputs from localStorage for: ${detectedFunctionName}`, {
                    inputsLength: inputs.length,
                    firstInput: inputs[0],
                    dataPreview: inputs[2]?.substring(0, 20) + '...'
                });
            } catch (error) {
                logger.error('Failed to parse function inputs from localStorage:', error);
                throw new Error('Failed to load transaction data. Please try executing again from Function Explorer.');
            }

            // Validate we have inputs
            if (inputs.length === 0) {
                throw new Error('No inputs found in stored transaction data.');
            }

            // Prepare transaction details
            const { txParams, txHash, nonce } = await prepareTxForSignature(contract, inputs);

            // Prepare for EIP-712 typed data signing
            logger.log("Preparing EIP-712 signature data");

            // Get the active chain ID - use the chainId prop passed from parent
            // This ensures consistency with the rest of the application
            const activeChainId = parseInt(chainId);

            logger.log("Using chainId from props:", activeChainId);

            // Validate that the chain ID is reasonable
            if (isNaN(activeChainId) || activeChainId <= 0) {
                throw new Error(`Invalid chain ID: ${chainId}`);
            }

            // Always use the chain ID from props for consistency
            const domain = {
                chainId: activeChainId,
                verifyingContract: selectedContract.address
            };

            logger.log("Using domain for signing");

            const types = {
                SafeTx: [
                    { name: 'to', type: 'address' },
                    { name: 'value', type: 'uint256' },
                    { name: 'data', type: 'bytes' },
                    { name: 'operation', type: 'uint8' },
                    { name: 'safeTxGas', type: 'uint256' },
                    { name: 'baseGas', type: 'uint256' },
                    { name: 'gasPrice', type: 'uint256' },
                    { name: 'gasToken', type: 'address' },
                    { name: 'refundReceiver', type: 'address' },
                    { name: 'nonce', type: 'uint256' }
                ]
            };

            // Ensure all values are properly formatted
            const typedValue = {
                to: txParams.to,
                value: txParams.value,
                data: txParams.data || "0x",
                operation: Number(txParams.operation),
                safeTxGas: ethers.BigNumber.from(txParams.safeTxGas || 0),
                baseGas: ethers.BigNumber.from(txParams.baseGas || 0),
                gasPrice: ethers.BigNumber.from(txParams.gasPrice || 0),
                gasToken: txParams.gasToken || ethers.constants.AddressZero,
                refundReceiver: txParams.refundReceiver || ethers.constants.AddressZero,
                nonce: ethers.BigNumber.from(nonce || 0)
            };

            logger.log("Typed data value object:", {
                to: typedValue.to,
                value: typedValue.value.toString(),
                dataLength: typedValue.data.length,
                operation: typedValue.operation,
                nonce: typedValue.nonce.toString()
            });

            // Check if any data needs decryption before signing
            try {
                // Import secureDataHandler functions using dynamic import
                const secureDataHandlerModule = await import('../../utils/secureDataHandler');
                const { isEncrypted, decryptData, hasCustomPassword } = secureDataHandlerModule;

                // Check if data is encrypted and needs decryption
                if (typedValue.data && typeof typedValue.data === 'string' && isEncrypted(typedValue.data)) {
                    logger.log("Transaction data is encrypted, attempting to decrypt before signing");

                    // Check if we have a password set
                    if (!hasCustomPassword()) {
                        // Show password dialog or prompt user for password
                        setStatus("Transaction data is encrypted. Please enter your password to decrypt.");

                        // You might want to show a password dialog here
                        // For now, we'll just alert the user
                        alert("Transaction data is encrypted. Please set your encryption password in the Function Builder tab before collecting signatures.");
                        throw new Error("Encryption password not set. Please set your password first.");
                    }

                    try {
                        // Attempt to decrypt
                        const decryptedData = decryptData(typedValue.data);
                        logger.log("Successfully decrypted transaction data");

                        // Update the typedValue with decrypted data
                        typedValue.data = decryptedData;
                    } catch (decryptError) {
                        logger.error("Failed to decrypt transaction data:", decryptError);
                        setStatus("Failed to decrypt transaction data. Please check your encryption password.");
                        throw new Error("Decryption failed. Please check your encryption password.");
                    }
                }

                // Request signature with proper error handling:
                // Wrap the signing in a try-catch with more detailed error handling
                let signature;
                try {
                    // Log the exact parameters being passed to _signTypedData for debugging
                    logger.log("Calling _signTypedData with parameters:", {
                        domainChainId: domain.chainId,
                        verifyingContract: domain.verifyingContract,
                        typesLength: Object.keys(types).length,
                        typedValueTo: typedValue.to,
                        typedValueDataLength: typedValue.data.length
                    });

                    // Check if signer is valid and has the _signTypedData method
                    if (!signer || typeof (signer as any)._signTypedData !== 'function') {
                        throw new Error('Invalid signer or signer does not support _signTypedData');
                    }

                    signature = await (signer as ethers.providers.JsonRpcSigner)._signTypedData(
                        domain,
                        types,
                        typedValue
                    );

                    // Verify the signature is a valid string
                    if (!signature || typeof signature !== 'string') {
                        throw new Error('Signature returned is not a valid string');
                    }
                } catch (innerError) {
                    // Check if this is a hardware wallet "device busy" error (expected behavior)
                    const errorMessage = innerError instanceof Error ? innerError.message : String(innerError);
                    const isHardwareWalletBusy =
                        errorMessage.includes('Device call in progress') ||
                        errorMessage.includes('device is busy') ||
                        errorMessage.includes('Ledger device') ||
                        errorMessage.includes('Trezor device');

                    if (isHardwareWalletBusy) {
                        // This is expected - hardware wallets need time to process
                        logger.debug("Hardware wallet busy (expected), signature will complete:", errorMessage);
                    } else {
                        // This is an unexpected error - log it fully
                        logger.error("Error in _signTypedData call:", {
                            error: innerError,
                            errorType: typeof innerError,
                            errorMessage: errorMessage,
                            stack: innerError instanceof Error ? innerError.stack : undefined
                        });
                    }
                    throw innerError; // Re-throw to be caught by the outer catch
                }

                if (signature) {
                    logger.log("✅ Signature successfully collected:", signature.substring(0, 10) + "...");

                    // Mark wallet state as signed and store signature in context
                    setWalletStates(prevStates =>
                        prevStates.map(state =>
                            state.currentOwner === walletState.currentOwner
                                ? { ...state, hasSignature: true }
                                : state
                        )
                    );

                    // Add to context AND localStorage for double persistence
                    addSignature(walletState.currentOwner, signature);

                    // Also update localStorage directly as a backup
                    try {
                        const existingSignatures = JSON.parse(localStorage.getItem('multisig_signatures') || '{}');
                        existingSignatures[walletState.currentOwner.toLowerCase()] = signature;
                        localStorage.setItem('multisig_signatures', JSON.stringify(existingSignatures));
                    } catch (storageError) {
                        logger.error("Error updating signatures in localStorage:", storageError);
                    }

                    setStatus(`Signature collected for ${walletState.currentOwner}`);

                    // After successful signature collection, we can remove the "in-progress" state
                    // But keep the overall signature collection state
                    localStorage.removeItem('signature_state_before_collect');
                }
            } catch (signError) {
                // Check if this is a hardware wallet "device busy" error (expected behavior)
                const errorMessage = signError instanceof Error ? signError.message : String(signError);
                const errorString = JSON.stringify(signError, Object.getOwnPropertyNames(signError));
                const isHardwareWalletBusy =
                    errorMessage.includes('Device call in progress') ||
                    errorMessage.includes('device is busy') ||
                    errorString.includes('Device call in progress') ||
                    errorString.includes('Ledger device') ||
                    errorString.includes('Trezor device');

                if (isHardwareWalletBusy) {
                    // This is expected hardware wallet behavior - log at debug level
                    logger.debug("Hardware wallet processing (expected):", errorMessage);
                    // Don't show error to user - signature likely completed successfully
                } else {
                    // This is an unexpected error - log it fully
                    logger.error("Error during EIP-712 signing:", {
                        error: signError,
                        errorType: typeof signError,
                        errorString: errorString,
                        errorJSON: errorString
                    });

                    // DO NOT clear persisted state so the user can try again
                    if (signError instanceof Error) {
                        setStatus(`Signature collection failed: ${signError.message}`);
                    } else {
                        setStatus('Signature collection failed: Unknown error');
                    }
                }
            }
        } catch (error: any) {
            // Enhanced error logging with more context
            logger.error(`Failed to collect signature:`, {
                error,
                errorType: typeof error,
                errorString: String(error),
                errorJSON: JSON.stringify(error, Object.getOwnPropertyNames(error)),
                stack: error?.stack
            });

            // More robust error message handling
            const errorMessage = error?.message || String(error) || 'Unknown error occurred';
            setStatus(`Signature collection failed: ${errorMessage}`);

            // Add debugging alert for development
            if (process.env.NODE_ENV === 'development') {
                console.error('Signature collection error details:', error);
            }
        }
    }

    useEffect(() => {
        if (tempSignatureState) {
            // Restore wallet states or other signature data from tempSignatureState
            if (tempSignatureState.walletStates) {
                setWalletStates(tempSignatureState.walletStates);
            }
            // Do not attempt to call setFunctionInputs since functionInputs comes via props
            if (tempSignatureState.showSignatureFlow) {
                setShowSignatureFlow(true);
            }
            logger.log("Restored persisted signature state");
        }
    }, [tempSignatureState]);

    useEffect(() => {
        // Check if we have saved signatures when component mounts or signer changes
        const loadSavedSignatures = () => {
            try {
                // Get saved signatures from localStorage or context
                const savedSigs = localStorage.getItem('multisig_signatures');
                if (savedSigs) {
                    const parsedSigs = JSON.parse(savedSigs);
                    logger.log("Loaded saved signatures:", Object.keys(parsedSigs).length);

                    // Update wallet states to show which owners have already signed
                    setWalletStates(prevStates =>
                        prevStates.map(state => ({
                            ...state,
                            hasSignature: !!parsedSigs[state.currentOwner.toLowerCase()]
                        }))
                    );
                }
            } catch (error) {
                logger.error("Error loading saved signatures:", error);
            }
        };

        loadSavedSignatures();
    }, [signer]); // Re-run when signer changes

    // First effect: Determine whether to show the signature flow UI
    useEffect(() => {
        // When component mounts or signer changes, check if we have pending signatures
        const savedProgress = localStorage.getItem('multisig_signature_progress');
        const savedSignatures = localStorage.getItem('multisig_signatures');

        if (savedProgress && savedSignatures) {
            try {
                const progress = JSON.parse(savedProgress);
                // If we have some signatures but not all required ones, show signature flow
                if (progress.collectedCount > 0 && progress.collectedCount < progress.requiredCount) {
                    setShowSignatureFlow(true);
                }
            } catch (e) {
                logger.error("Error restoring signature state:", e);
            }
        }
    }, [signer]); // Re-run when signer changes

    // Modified to be a verification step
    const completeSigning = async () => {
        try {
            // Get all signatures from the context
            const collectedSignatures = getAllSignatures();

            // CRITICAL FIX: Sort signatures by signer address (ascending) BEFORE passing to callback
            // Safe contracts require signatures to be in ascending order by signer address
            const sortedSignatures = [...collectedSignatures].sort((a, b) => {
                const addressA = a.signer.toLowerCase();
                const addressB = b.signer.toLowerCase();
                if (addressA < addressB) return -1;
                if (addressA > addressB) return 1;
                return 0;
            });

            logger.log("🔍 SIGNATURE SORTING DEBUG:", {
                originalOrder: collectedSignatures.map(s => ({
                    address: s.signer,
                    sigPrefix: s.signature.substring(0, 20) + '...'
                })),
                sortedOrder: sortedSignatures.map(s => ({
                    address: s.signer,
                    sigPrefix: s.signature.substring(0, 20) + '...'
                })),
                sortingChanged: JSON.stringify(collectedSignatures.map(s => s.signer)) !== JSON.stringify(sortedSignatures.map(s => s.signer))
            });

            // Verify we have enough signatures
            if (sortedSignatures.length < threshold) {
                throw new Error(`Not enough signatures: collected ${sortedSignatures.length}, need ${threshold}`);
            }

            // Verify all signatures are valid
            const allValid = await verifyAllSignatures(sortedSignatures.map(s => s.signature));

            logger.log("Calling onSignaturesCollected with", sortedSignatures.length, "sorted signatures");

            if (allValid) {
                // This is the important part - actually call the parent handler with SORTED signatures
                onSignaturesCollected(sortedSignatures);
                setStatus('All signatures verified and collected successfully');

                // Add clear confirmation to user
                setExecutionStatus('Signatures collected successfully. Preparing transaction...');

                // Clear the persisted signature state now that the process is complete
                setTempSignatureState(null);

                // Explicitly trigger wallet interaction
                logger.log("Initiating transaction execution with collected signatures");
            } else {
                throw new Error('Signature verification failed');
            }
        } catch (error: any) {
            logger.error("Error in final signature verification:", error);
            setExecutionStatus(`Signature verification failed: ${error.message}`);
            throw error;
        }
    };

    // Add this helper function
    const verifyAllSignatures = async (signatures: string[]): Promise<boolean> => {
        try {
            // Add your signature verification logic here
            // For example, checking against the contract's requirements
            return true;
        } catch (error) {
            logger.error("Signature verification error:", error);
            return false;
        }
    };

    const chooseWalletConnectorForAddress = async (address: string) => {
        const walletType = KNOWN_WALLET_ADDRESSES[address.toLowerCase()];

        if (!walletType) {
            throw new Error(`No wallet type configuration found for address: ${address}`);
        }

        // Check if already connected to the correct wallet
        if (connector) {
            // Raw comparison for debugging
            logger.log("Address comparison (raw):", {
                currentConnectedAddressRaw: account,
                requiredAddressRaw: address,
                isMatchRaw: account === address
            });

            // Normalized comparison (main logic)
            const currentAddress = account ? account.toLowerCase() : "";
            const targetAddress = address.toLowerCase();

            logger.log("Address comparison (normalized):", {
                currentAddress,
                targetAddress,
                isMatch: currentAddress === targetAddress
            });

            // Only compare normalized addresses
            if (currentAddress === targetAddress) {
                logger.log("Already connected to the correct wallet ✅");
                return connector; // Already connected to this wallet
            }

            // Only show alert if addresses don't match
            alert(`Please switch to the wallet address ${address} in your ${walletType === 'coinbase' ? 'Coinbase Wallet' : 'MetaMask'} extension.`);

            // Return the current connector anyway, to avoid recreating it
            return connector;
        }

        // If no connector exists yet, create one based on wallet type
        logger.log("Creating new connector for wallet type:", walletType);

        switch (walletType) {
            case 'metamask':
                return new MetaMask({
                    actions: {
                        startActivation: () => () => { },
                        update: () => { },
                        resetState: () => { }
                    },
                    onError: logger.error
                });
            case 'coinbase':
                return new CoinbaseWallet({
                    actions: {
                        startActivation: () => () => { },
                        update: ({ chainId, accounts }) => {
                            logger.log('Wallet connector updated:', { chainId, accounts });
                        },
                        resetState: () => { }
                    },
                    options: {
                        appName: 'Memer MultiSig',
                        url: window.location.origin
                    },
                    onError: logger.error
                });
            default:
                throw new Error(`No wallet connector found for address: ${address}`);
        }
    };

    const connectCoinbaseWallet = async (ownerAddress: string, chainId: number) => {
        try {
            const rpcUrl = RPC_URLS[chainId.toString()];
            if (!rpcUrl) throw new Error(`No RPC URL for chain ID ${chainId}`);

            // Check if Coinbase Wallet is already injected
            if ((window.ethereum as any)?.isCoinbaseWallet) {
                const accounts = await (window.ethereum as any).request({
                    method: 'eth_requestAccounts'
                });

                if (!accounts.includes(ownerAddress.toLowerCase())) {
                    throw new Error('Connected account does not match required owner');
                }

                return window.ethereum;
            }

            // Coinbase Wallet initialization 
            const coinbaseWallet = new CoinbaseWallet({
                actions: {
                    startActivation: () => () => { },
                    update: ({ chainId, accounts }) => {
                        logger.log('Wallet connector updated:', { chainId, accounts });
                    },
                    resetState: () => { }
                },
                options: {
                    appName: 'Memer MultiSig',
                    url: window.location.origin // or "http://localhost:3000" if in local dev
                },
                onError: logger.error
            });

            // Activate the wallet with the specific chain ID
            await coinbaseWallet.activate(chainId);

            const provider = (coinbaseWallet as any).walletSDK.makeWeb3Provider({
                jsonRpcUrl: RPC_URLS[chainId],
                chainId: chainId.toString()
            });

            const accounts = await provider.request({
                method: 'eth_requestAccounts'
            });
            if (!(accounts as string[])?.map(a => a.toLowerCase()).includes(ownerAddress.toLowerCase())) {
                throw new Error('Connected account does not match required owner');
            }

            setStatus(`Coinbase wallet connected for ${ownerAddress}`);
            return provider;
        } catch (error: any) {
            logger.error("Coinbase wallet connection failed:", error);
            setStatus(`Failed to connect Coinbase wallet: ${error.message}`);
            throw error;
        }
    };

    // Add error handling for address loading
    if (addressesError) {
        return (
            <Box sx={{ mt: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load wallet configuration: {addressesError}
                </Alert>
                <Typography variant="body2">
                    Please refresh the page or contact support if the problem persists.
                </Typography>
            </Box>
        );
    }

    // Show loading state while fetching addresses
    if (addressesLoading) {
        return (
            <Box sx={{ mt: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                    Loading Wallet Configuration...
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography>Loading secure wallet addresses...</Typography>
                </Box>
            </Box>
        );
    }

    // Handle null signer gracefully - show progress but disable signature collection
    if (!signer) {
        return (
            <Box sx={{ mt: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                    Collecting MultiSig Signatures
                </Typography>

                {/* Embedded Wallet Switcher - allows switching without leaving signature flow */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                        Switch wallets here to collect signatures from different owners:
                    </Typography>
                    <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
                        💡 Tip: You can connect hardware wallets (Ledger/Trezor) through your browser extensions for maximum security
                    </Typography>
                    <RainbowKitCustomConnectButton />
                </Box>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    Please connect your wallet to continue signature collection.
                    Your progress has been saved and will be restored when you reconnect.
                </Alert>

                {/* Still show signature progress even without signer */}
                <SignatureProgressDisplay />

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Signatures collected: {Object.keys(contextSignatures).length} of {threshold}
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Return the JSX for the component (when signer is available)
    return (
        <Box sx={{ mt: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
                Collecting MultiSig Signatures
            </Typography>

            {/* Embedded Wallet Switcher - allows switching without leaving signature flow */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                    Switch wallets here to collect signatures from different owners:
                </Typography>
                <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
                    💡 Tip: You can connect hardware wallets (Ledger/Trezor) through your browser extensions for maximum security
                </Typography>
                <RainbowKitCustomConnectButton />
            </Box>

            {/* Smart Recovery Warning - only shows if legitimately interrupted */}
            {showRecoveryWarning && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        // Dismiss the warning and clean up
                        setShowRecoveryWarning(false);
                        localStorage.removeItem('signature_state_before_collect');
                        logger.log("Recovery warning dismissed by user");
                    }}
                >
                    It looks like your signature process was interrupted. You have {Object.keys(contextSignatures).length} of {threshold} signatures collected.
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{ ml: 2 }}
                        onClick={() => {
                            // Force restore from saved state
                            try {
                                const state = JSON.parse(localStorage.getItem('signature_state_before_collect') || '{}');
                                // Restore wallets state from localStorage
                                if (state.walletStates) {
                                    setWalletStates(state.walletStates);
                                }
                                // Other restoration logic
                                setShowSignatureFlow(true);
                                setShowRecoveryWarning(false);
                                logger.log("Signature process recovered from saved state");
                            } catch (e) {
                                logger.error("Recovery failed:", e);
                                setShowRecoveryWarning(false);
                            }
                        }}
                    >
                        Recover Progress
                    </Button>
                </Alert>
            )}

            {/* Add the SignatureProgressDisplay component */}
            <SignatureProgressDisplay />

            <Grid2 container spacing={2}>
                {walletStates.map((state, index) => (
                    <Grid2 component="div" flex={1} key={state.currentOwner}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1">
                                    Owner {index + 1}: {state.currentOwner.substring(0, 6)}...{state.currentOwner.substring(state.currentOwner.length - 4)}
                                </Typography>
                                <Tooltip title={state.currentOwner}>
                                    <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        Full: {state.currentOwner}
                                    </Typography>
                                </Tooltip>
                                <Typography variant="body2" color="text.secondary">
                                    Required Wallet: {state.requiredWallet}
                                </Typography>
                                {state.needsExtensionSwitch && (
                                    <Chip
                                        label={`Needs ${state.requiredWallet} extension`}
                                        color="warning"
                                        size="small"
                                        sx={{ mt: 1, mr: 1 }}
                                    />
                                )}
                                <Typography
                                    variant="body2"
                                    color={state.hasSignature ? "success.main" : "warning.main"}
                                    sx={{ mt: 1 }}
                                >
                                    Status: {state.hasSignature ? '✓ Signed' : 'Pending'}
                                </Typography>

                                {!state.hasSignature && (
                                    <Button
                                        type="button" // ensure it does not submit any form
                                        variant="contained"
                                        color="primary"
                                        onClick={() => collectSignature(state)}
                                        sx={{ mt: 2 }}
                                    >
                                        Collect Signature
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid2>
                ))}
            </Grid2>

            {walletStates.filter(state => state.hasSignature).length >= threshold && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={completeSigning}
                    disabled={walletStates.filter(s => s.hasSignature).length < threshold}
                    sx={{ mt: 3 }}
                >
                    Complete Transaction with {walletStates.filter(s => s.hasSignature).length} / {threshold} Signatures
                </Button>
            )}

            {status && (
                <MuiAlert
                    severity={status.includes('Error') ? 'error' : status.includes('collected') ? 'success' : 'info'}
                    sx={{ mt: 3 }}
                >
                    {status}
                </MuiAlert>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Important: To collect multiple signatures, switch wallets in your browser extension for each owner.
                    Your signature progress will be preserved between wallet changes.
                </Typography>

                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                        try {
                            logger.log('🔄 Resetting signature process...');

                            // Use the context's clearAllStoredData function to properly clear all signature state
                            clearAllStoredData();

                            // Clear additional localStorage items that might be causing issues
                            localStorage.removeItem('signature_flow_active');
                            localStorage.removeItem('function_inputs');
                            localStorage.removeItem('signature_state_before_collect');
                            localStorage.removeItem('multisig_signatures');
                            localStorage.removeItem('multisig_signature_progress');
                            localStorage.removeItem('current_signature_flow_key');

                            // Reset UI state without causing logout
                            setWalletStates(walletStates.map(state => ({
                                ...state,
                                hasSignature: false,
                                isConnected: false,
                                needsWalletSwitch: false,
                                needsExtensionSwitch: false
                            })));

                            setStatus('Signature collection process reset. You can start fresh.');
                            setExecutionStatus('');

                            // Clear any persisted signature state in temp storage
                            setTempSignatureState(null);

                            logger.log('✅ Signature process reset completed');

                            // Don't reload the page - just reset the state
                            // This prevents the logout issue
                        } catch (error) {
                            logger.error('Error resetting signature process:', error);
                            setStatus('Error resetting signature process. Please refresh the page manually.');
                        }
                    }}
                    sx={{ mt: 2 }}
                >
                    Reset Signature Process
                </Button>
            </Box>
        </Box>
    );
};

export default MultiSigWalletSignatureFlow;
